// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Navigation      from './components/Navigation';
import Home            from './components/Home';
import PublicHome      from './components/PublicHome';
import FareCalculator  from './components/FareCalculator';
import BecomeAMember   from './components/BecomeAMember';
import Login           from './components/Login';
import ThankYou        from './components/ThankYou';
import SetPassword     from './components/SetPassword';
import ResetPassword   from './components/ResetPassword';
import ProtectedRoute  from './components/ProtectedRoute';
import ManageUsers     from './components/ManageUsers';
import ViewLeads       from './components/ViewLeads';
import ViewReports     from './components/ViewReports';
import UserDashboard   from './components/UserDashboard';
import UpdateAccount   from './components/UpdateAccount';
import AcceptedJobs    from './components/AcceptedJobs';
import CompletedJobs   from './components/CompletedJobs';
import UncompletedJobs from './components/UncompletedJobs';
import ReAssignedJobs  from './components/ReAssignedJobs';
import DateTimePickerDemo from './components/DateTimePickerDemo';
import TestDateTimePicker from './components/TestDateTimePicker';
import SimpleDateTimeTest from './components/SimpleDateTimeTest';
import MinimalLeadForm from './components/MinimalLeadForm';
import WorkingTest from './components/WorkingTest';
import AcceptedJobsSimple from './components/AcceptedJobsSimple';
import JobMonitor from './components/JobMonitor';
import JobMonitorTest from './components/JobMonitorTest';
import DriverStats from './components/DriverStats';
import Training from './components/Training';
import Troubleshooting from './components/Troubleshooting';
import Downloads from './components/Downloads';
import AdminTroubleshooting from './components/AdminTroubleshooting';
import SystemAdmin from './components/SystemAdmin';
import GoCardlessDirectDebit from './components/GoCardlessDirectDebit';
import GoCardlessSuccess from './components/GoCardlessSuccess';
import ProfileCompletionEnforcement from './components/ProfileCompletionEnforcement';
import BookingLinkManager from './components/BookingLinkManager';
import BookingLinkForm from './components/BookingLinkForm';
import BookingSuccess from './components/BookingSuccess';
import Referrals from './components/Referrals';
import ReferralSignup from './components/ReferralSignup';
import DispatchModule from './components/DispatchModule';
import DispatchLogin from './components/DispatchLogin';
import CustomerBookingDemo from './components/CustomerBookingDemo';
import Merchandise from './components/Merchandise';
import DriverEmailLog from './components/DriverEmailLog';
import GoogleMapsDiagnostic from './components/GoogleMapsDiagnostic';
import PublicDriverLocationMap from './components/PublicDriverLocationMap';
import AudioTest from './components/AudioTest';
import CustomerManagement from './components/CustomerManagement';
import CustomerReviewForm from './components/CustomerReviewForm';
import MyDriverReviews from './components/MyDriverReviews';
import VehicleExpiryWarningModal from './components/VehicleExpiryWarningModal';
import TaxiMeter from './components/TaxiMeter';
import PrivacyPolicy from './components/PrivacyPolicy';
import CookiePolicy from './components/CookiePolicy';
import TermsAndConditions from './components/TermsAndConditions';
import Footer from './components/Footer';
// REMOVED: WhereIsMyTaxi component - driver location tracking disabled to reduce Google API costs
// import WhereIsMyTaxi from './components/WhereIsMyTaxi';
import CustomerBookings from './components/CustomerBookings';
import useMyLinkJobPopup from './hooks/useMyLinkJobPopup';

export default function App() {
  // Initialize MyLink job popup system
  const { PopupComponent } = useMyLinkJobPopup();
  const [showVehicleExpiryWarning, setShowVehicleExpiryWarning] = useState(false);
  const [expiringDocuments, setExpiringDocuments] = useState([]);
  const [vehicleWarningShownThisSession, setVehicleWarningShownThisSession] = useState(false);  
  // Prevent zoom via keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Prevent zoom shortcuts
      if ((event.ctrlKey || event.metaKey) && (event.key === '+' || event.key === '-' || event.key === '=' || event.key === '0')) {
        event.preventDefault();
        return false;
      }
      
      // Prevent F11 fullscreen
      if (event.key === 'F11') {
        event.preventDefault();
        return false;
      }
    };

    const handleWheel = (event) => {
      // Prevent zoom with Ctrl+Wheel
      if (event.ctrlKey) {
        event.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown, { passive: false });
    document.addEventListener('wheel', handleWheel, { passive: false });

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Sleep mode detection and audio alert
  useEffect(() => {
    let wasHidden = false;
    let audioTimeout = null;
    let audioInterval = null;
    let cachedAudioData = null; // Cache audio data for network failures
    let cachedAudioBuffer = null; // Cache the actual audio file buffer
    let audioPlayCount = 0; // Track number of audio plays
    let maxMappedAudioPlays = 3; // Maximum plays of Re-Open.mp3
    let maxBeepPlays = 10; // Maximum plays of double beep
    let totalMaxPlays = maxMappedAudioPlays + maxBeepPlays; // Total maximum plays
    
    // Track user interaction for Android audio requirements
    const trackUserInteraction = () => {
      // Remove listeners after first interaction
      document.removeEventListener('click', trackUserInteraction);
      document.removeEventListener('touchstart', trackUserInteraction);
      document.removeEventListener('keydown', trackUserInteraction);
    };
    
    // Add user interaction listeners for Android
    document.addEventListener('click', trackUserInteraction);
    document.addEventListener('touchstart', trackUserInteraction);
    document.addEventListener('keydown', trackUserInteraction);

    const handleVisibilityChange = () => {
      const isHidden = document.hidden;
      
      if (isHidden && !wasHidden) {
        // App went to sleep/background - START 3-SECOND TIMER
        wasHidden = true;
        
        // Clear any existing timers
        if (audioTimeout) clearTimeout(audioTimeout);
        if (audioInterval) clearInterval(audioInterval);
        
        // Play audio after 1 minute
        audioTimeout = setTimeout(() => {
          if (wasHidden) { // Only play if still hidden after 1 minute
            playSleepAudio().catch(error => {
              console.error('Sleep audio failed:', error);
            });
            
            // Then repeat every 30 seconds
            audioInterval = setInterval(() => {
              if (wasHidden) { // Only repeat if still hidden
                playSleepAudio().catch(error => {
                  console.error('Repeating sleep audio failed:', error);
                });
              }
            }, 30000); // 30 seconds
          }
        }, 60000); // 1 minute (60 seconds)
        
      } else if (!isHidden && wasHidden) {
        // App came back from sleep/background - STOP ALL TIMERS
        wasHidden = false;
        audioPlayCount = 0; // Reset play count when app becomes active
        
        // Clear all timers
        if (audioTimeout) {
          clearTimeout(audioTimeout);
          audioTimeout = null;
        }
        if (audioInterval) {
          clearInterval(audioInterval);
          audioInterval = null;
        }
      }
    };

    const playSleepAudio = async () => {
      // Check if we've exceeded the maximum number of plays
      if (audioPlayCount >= totalMaxPlays) {
        console.log('🔇 Maximum sleep audio plays reached, stopping reminders');
        return;
      }
      
      audioPlayCount++; // Increment play count
      console.log(`🔊 Sleep audio play #${audioPlayCount} of ${totalMaxPlays} (mapped: ${maxMappedAudioPlays}, beep: ${maxBeepPlays})`);
      
      try {
        // First 3 plays: try to get mapped audio for sleep mode (Re-Open.mp3)
        if (audioPlayCount <= maxMappedAudioPlays) {
          console.log(`🎵 Attempting to play mapped audio (Re-Open.mp3) for play #${audioPlayCount}`);
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
          
          const response = await fetch('/api/admin/audio-mappings/function/sleep_mode', {
            signal: controller.signal,
            cache: 'no-cache'
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ Mapped audio data received:', data);
            cachedAudioData = data; // Cache the data for future use
            
            // Android-specific: Use Web Audio API for mapped audio if on Android
            if (navigator.userAgent.includes('Android') || navigator.userAgent.includes('Mobile')) {
              try {
                console.log('📱 Using Android Web Audio API for mapped audio');
                await playAndroidMappedSleepAudio(data.audioFile, true); // Cache buffer
                console.log('✅ Android mapped audio played successfully');
                return;
              } catch (error) {
                console.error('❌ Android mapped audio failed:', error);
                // Fallback to standard audio
              }
            }
            
            // Standard audio for non-Android or fallback
            console.log('🎵 Using standard HTML5 Audio for mapped audio');
            const audio = new Audio(data.audioFile.url);
            audio.volume = 0.8;
            
            audio.play().catch(error => {
              console.error('❌ Standard mapped audio failed:', error);
              playDefaultSleepAudio();
            });
            console.log('✅ Standard mapped audio started');
            return;
          } else {
            console.log(`❌ Mapped audio API failed: ${response.status} ${response.statusText}`);
          }
        }
        
        // After 3 mapped audio plays: switch to double beep
        if (audioPlayCount <= totalMaxPlays) {
          console.log(`🔔 Playing double beep for play #${audioPlayCount} (after mapped audio phase)`);
          // Android-specific: Use Web Audio API for double beep if on Android
          if (navigator.userAgent.includes('Android') || navigator.userAgent.includes('Mobile')) {
            try {
              console.log('📱 Using Android Web Audio API for double beep');
              await playAndroidDoubleBeep();
              console.log('✅ Android double beep played successfully');
              return;
            } catch (error) {
              console.error('❌ Android double beep failed:', error);
              // Fallback to standard audio
            }
          }
          console.log('🎵 Using standard double beep');
          playDefaultSleepAudio();
        }
      } catch (error) {
        console.error('❌ Sleep audio error:', error);
        // Network failure - try to use cached audio data (only for first 3 plays)
        if (audioPlayCount <= maxMappedAudioPlays && cachedAudioData && cachedAudioData.audioFile) {
          console.log('🔄 Using cached audio data for mapped audio');
          // Android-specific: Use Web Audio API for cached audio if on Android
          if (navigator.userAgent.includes('Android') || navigator.userAgent.includes('Mobile')) {
            try {
              console.log('📱 Using cached Android mapped audio');
              await playAndroidMappedSleepAudio(cachedAudioData.audioFile, false); // Use cached buffer
              console.log('✅ Cached Android mapped audio played successfully');
              return;
            } catch (cacheError) {
              console.error('❌ Cached Android mapped audio failed:', cacheError);
              // Fallback to beep
            }
          }
          
          // Standard audio for cached data
          try {
            console.log('🎵 Using cached standard audio');
            const audio = new Audio(cachedAudioData.audioFile.url);
            audio.volume = 0.8;
            await audio.play();
            console.log('✅ Cached standard audio played successfully');
            return;
          } catch (cacheError) {
            console.error('❌ Cached standard audio failed:', cacheError);
            // Fallback to beep
          }
        }
        
        // Final fallback to double beep or default audio
        if (audioPlayCount <= totalMaxPlays) {
          console.log(`🔔 Final fallback to double beep for play #${audioPlayCount}`);
          if (navigator.userAgent.includes('Android') || navigator.userAgent.includes('Mobile')) {
            try {
              console.log('📱 Final fallback: Android double beep');
              await playAndroidDoubleBeep();
              console.log('✅ Final fallback Android double beep played successfully');
              return;
            } catch (beepError) {
              console.error('❌ Final fallback Android double beep failed:', beepError);
              // Final fallback
            }
          }
          console.log('🎵 Final fallback: standard double beep');
          playDefaultSleepAudio();
        }
      }
    };

    // Android-specific mapped sleep audio using Web Audio API
    const playAndroidMappedSleepAudio = async (audioFile, shouldCache = false) => {
      try {
        // Create or resume AudioContext (required for Android background audio)
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();
        
        // Resume context if suspended (Android requirement)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        
        let audioBuffer;
        
        // Check if we have a cached buffer and no network access needed
        if (cachedAudioBuffer && !shouldCache) {
          audioBuffer = cachedAudioBuffer;
        } else {
          // Load and decode the mapped audio file
          const response = await fetch(audioFile.url);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch audio file: ${response.status} ${response.statusText}`);
          }
          
          const arrayBuffer = await response.arrayBuffer();
          audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Cache the buffer if requested
          if (shouldCache) {
            cachedAudioBuffer = audioBuffer;
          }
        }
        
        // Create audio source from the mapped file
        const source = audioContext.createBufferSource();
        const gainNode = audioContext.createGain();
        
        source.buffer = audioBuffer;
        source.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Set volume for Android compatibility
        gainNode.gain.setValueAtTime(0.8, audioContext.currentTime);
        
        // Play the mapped audio
        source.start(audioContext.currentTime);
        
        // Close context after audio finishes
        setTimeout(() => {
          if (audioContext.state !== 'closed') {
            audioContext.close();
          }
        }, audioBuffer.duration * 1000 + 1000);
        
      } catch (error) {
        throw error;
      }
    };

    // Android-specific sleep audio using Web Audio API (fallback)

    // Android-specific double beep for sleep mode (after 3 mapped audio plays)
    const playAndroidDoubleBeep = async () => {
      try {
        // Create or resume AudioContext (required for Android background audio)
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();
        
        // Resume context if suspended (Android requirement)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        
        // Create double beep pattern
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Create double beep pattern (two distinct beeps)
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
        
        // First beep
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.6, audioContext.currentTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.2);
        
        // Second beep (after short pause)
        gainNode.gain.linearRampToValueAtTime(0.6, audioContext.currentTime + 0.3);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
        
        // Close context after use to prevent memory leaks
        setTimeout(() => {
          if (audioContext.state !== 'closed') {
            audioContext.close();
          }
        }, 1000);
        
      } catch (error) {
        throw error;
      }
    };

    const playDefaultSleepAudio = () => {
      try {
        // For non-Android devices, use Web Audio API to create double beep
        if (typeof window.AudioContext !== 'undefined' || typeof window.webkitAudioContext !== 'undefined') {
          const AudioContext = window.AudioContext || window.webkitAudioContext;
          const audioContext = new AudioContext();
          
          // Create double beep pattern
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          // Create double beep pattern (two distinct beeps)
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
          
          // First beep
          gainNode.gain.setValueAtTime(0, audioContext.currentTime);
          gainNode.gain.linearRampToValueAtTime(0.6, audioContext.currentTime + 0.01);
          gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.2);
          
          // Second beep (after short pause)
          gainNode.gain.linearRampToValueAtTime(0.6, audioContext.currentTime + 0.3);
          gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.5);
          
          // Close context after use to prevent memory leaks
          setTimeout(() => {
            if (audioContext.state !== 'closed') {
              audioContext.close();
            }
          }, 1000);
        } else {
          // Fallback to audio file if Web Audio API not available
          const audio = new Audio(`/NewJob_MyLink.mp3?t=${Date.now()}`);
          audio.volume = 0.8;
          
          audio.play().catch(error => {
            console.error('Default audio failed:', error);
          });
        }
      } catch (error) {
        console.error('Error in playDefaultSleepAudio function:', error);
      }
    };

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Add test function to window for manual testing
    window.testSleepAudio = () => {
      playSleepAudio();
    };


    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Clear any active timers on cleanup
      if (audioTimeout) clearTimeout(audioTimeout);
      if (audioInterval) clearInterval(audioInterval);
    };
  }, []);

  // Check vehicle document expiry dates on app startup and after login
  useEffect(() => {
    const checkVehicleExpiry = async () => {
      try {
        const token = localStorage.getItem('token');
        const role = localStorage.getItem('role');
        
        // Only check for professional and listing_only users (drivers)
        if (!token || (role !== 'professional' && role !== 'listing_only')) {
          return; // User not logged in or not a driver
        }
        
        // Don't show warning on login page - wait for actual login event
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/') {
          return; // Don't check on login page
        }

        // Small delay to ensure navigation has completed after login
        await new Promise(resolve => setTimeout(resolve, 1500));

        const response = await axios.get('/auth/check-vehicle-expiry', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data.hasExpiringDocuments && !vehicleWarningShownThisSession) {
          setExpiringDocuments(response.data.expiringDocuments);
          setShowVehicleExpiryWarning(true);
          setVehicleWarningShownThisSession(true);
        }
      } catch (error) {
        console.error('Error checking vehicle expiry:', error);
        // Silently fail - don't block app startup
      }
    };

    // Don't check immediately on app startup - only on login event
    // This prevents the warning from showing before user logs in
    
    // Listen for login event
    const handleUserLoggedIn = () => {
      console.log('User logged in, checking vehicle expiry...');
      // Reset the session flag on new login
      setVehicleWarningShownThisSession(false);
      // Delay the check to ensure navigation completes
      setTimeout(() => {
        checkVehicleExpiry();
      }, 1500);
    };
    
    // Listen for logout event to reset session flag
    const handleUserLoggedOut = () => {
      console.log('User logged out, resetting vehicle warning flag...');
      setVehicleWarningShownThisSession(false);
      setShowVehicleExpiryWarning(false);
    };
    
    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    window.addEventListener('userLoggedOut', handleUserLoggedOut);
    
    return () => {
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
      window.removeEventListener('userLoggedOut', handleUserLoggedOut);
    };
  }, [vehicleWarningShownThisSession]);

  const handleVehicleExpiryUpdate = async () => {
    // Re-check vehicle expiry after update
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await axios.get('/auth/check-vehicle-expiry', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.hasExpiringDocuments) {
        setExpiringDocuments(response.data.expiringDocuments);
        setShowVehicleExpiryWarning(true);
      } else {
        setShowVehicleExpiryWarning(false);
      }
    } catch (error) {
      console.error('Error re-checking vehicle expiry:', error);
    }
  };

  // Add error handling for debugging
  try {
    return (
    <>
      <VehicleExpiryWarningModal
        show={showVehicleExpiryWarning}
        expiringDocuments={expiringDocuments}
        onClose={() => {
          setShowVehicleExpiryWarning(false);
          // Don't reset session flag on close - keep it true so warning doesn't reappear this session
        }}
        onUpdate={handleVehicleExpiryUpdate}
      />
      <Router>
      <style>{`
        /* Disable zoom for entire app except Google Maps */
        html, body {
          touch-action: pan-x pan-y;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
        }
        
        /* Allow zoom only for Google Maps */
        .gm-style, .gm-style * {
          touch-action: auto !important;
        }
        
        /* Prevent zoom on double-tap */
        * {
          touch-action: manipulation;
        }
        
        /* Allow Google Maps to handle its own zoom */
        .gm-style {
          touch-action: auto !important;
        }
        
        .gm-style div, .gm-style span, .gm-style label, .gm-style input {
          touch-action: auto !important;
        }
      `}</style>
      <Navigation />
      <div className="container mt-4">
        <Routes>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<PublicHome />} />
          <Route path="/fare-calculator" element={<FareCalculator />} />
          <Route path="/become-a-member" element={<BecomeAMember />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dispatch-login" element={<DispatchLogin />} />
          <Route path="/thank-you" element={<ThankYou />} />
                        <Route path="/set-password" element={<SetPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/gocardless-direct-debit" element={<GoCardlessDirectDebit />} />
              <Route path="/gocardless-success" element={<GoCardlessSuccess />} />
              <Route path="/profile-completion" element={<ProfileCompletionEnforcement />} />
          <Route path="/book/:linkId" element={<BookingLinkForm />} />
          <Route path="/booking-success" element={<BookingSuccess />} />
          <Route path="/referral-signup" element={<ReferralSignup />} />
          <Route path="/customer-booking-demo" element={<CustomerBookingDemo />} />
          <Route path="/google-maps-diagnostic" element={<GoogleMapsDiagnostic />} />
          <Route path="/public-driver-locations" element={<PublicDriverLocationMap />} />
          {/* REMOVED: Where Is My Driver tracking - disabled to reduce Google API costs */}
          {/* <Route path="/where-is-my-taxi" element={<WhereIsMyTaxi />} /> */}
        <Route path="/my-bookings" element={<CustomerBookings />} />
          <Route path="/review-driver/:jobId" element={<CustomerReviewForm />} />
          
          {/* Legal Documents - Public Access */}
          <Route path="/privacy-policy" element={<PrivacyPolicy isPage={true} />} />
          <Route path="/cookies-policy" element={<CookiePolicy isPage={true} />} />
          <Route path="/terms" element={<TermsAndConditions isPage={true} />} />

          {/* Admin - Redirect to System Administration */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Navigate to="/admin/system" replace />
            </ProtectedRoute>
          }/>
          <Route path="/admin/manage-users" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ManageUsers />
            </ProtectedRoute>
          }/>
          <Route path="/admin/view-leads" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ViewLeads />
            </ProtectedRoute>
          }/>
          <Route path="/admin/view-reports" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ViewReports />
            </ProtectedRoute>
          }/>
          <Route path="/admin/troubleshooting" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminTroubleshooting />
            </ProtectedRoute>
          }/>
          <Route path="/admin/system" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SystemAdmin />
            </ProtectedRoute>
          }/>
          <Route path="/admin/audio-test" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AudioTest />
            </ProtectedRoute>
          }/>

          {/* Professional & Listing Only */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['professional', 'listing_only']}>
              <UserDashboard />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/update-account" element={
            <ProtectedRoute allowedRoles={['professional', 'listing_only']}>
              <UpdateAccount />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/accepted-jobs" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <AcceptedJobs />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/completed-jobs" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <CompletedJobs />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/uncompleted-jobs" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <UncompletedJobs />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/reassigned-jobs" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <ReAssignedJobs />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/job-monitor" element={
            <ProtectedRoute allowedRoles={['admin', 'professional', 'listing_only']}>
              <JobMonitor />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/driver-stats" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <DriverStats />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/training" element={
            <ProtectedRoute allowedRoles={['professional', 'listing_only']}>
              <Training />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/troubleshooting" element={
            <ProtectedRoute allowedRoles={['professional', 'listing_only']}>
              <Troubleshooting />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/downloads" element={
            <ProtectedRoute allowedRoles={['professional', 'listing_only']}>
              <Downloads />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/booking-links" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <BookingLinkManager />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/referrals" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <Referrals />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/merchandise" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <Merchandise />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/email-log" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <DriverEmailLog />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/taxi-meter" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <TaxiMeter />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/customer-management" element={
            <ProtectedRoute allowedRoles={['professional', 'admin']}>
              <CustomerManagement />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/my-reviews" element={
            <ProtectedRoute allowedRoles={['professional']}>
              <MyDriverReviews />
            </ProtectedRoute>
          }/>
          <Route path="/dashboard/dispatch" element={
            <ProtectedRoute allowedRoles={['dispatch']}>
              <DispatchModule />
            </ProtectedRoute>
          }/>

          {/* Demo Routes */}
                           <Route path="/datetime-picker-demo" element={<DateTimePickerDemo />} />
                 <Route path="/test-datetime-picker" element={<TestDateTimePicker />} />
                 <Route path="/simple-datetime-test" element={<SimpleDateTimeTest />} />
                 <Route path="/minimal-lead-form" element={<MinimalLeadForm />} />
                 <Route path="/working-test" element={<WorkingTest />} />
                 <Route path="/accepted-jobs-simple" element={<AcceptedJobsSimple />} />
                 <Route path="/job-monitor-test" element={<JobMonitorTest />} />

          {/* Fallback */}
          <Route path="*" element={<h2>404 – Page Not Found</h2>} />
        </Routes>
      </div>
      <Footer />
      {PopupComponent}
    </Router>
    </>
    );
  } catch (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Application Error</h2>
        <p>There was an error loading the application.</p>
        <button onClick={() => window.location.reload()}>Reload Page</button>
      </div>
    );
  }
}
